/**
 * The seven-state machine that every task moves through.
 *
 * The lifecycle is invariant — it does not branch on actor (ai vs human).
 * Only the work *inside* a state varies. See packages/shared/src/profiles.ts
 * for the per-state actor configuration.
 */
export const TASK_STATES = [
  'triage',
  'plan',
  'execute',
  'paused',
  'succeeded',
  'failed',
  'cancelled',
] as const;

export type TaskState = (typeof TASK_STATES)[number];

export const WORK_STATES = ['triage', 'plan', 'execute'] as const;
export type WorkState = (typeof WORK_STATES)[number];

export const TERMINAL_STATES = ['succeeded', 'failed', 'cancelled'] as const;
export type TerminalState = (typeof TERMINAL_STATES)[number];

export function isWorkState(state: TaskState): state is WorkState {
  return WORK_STATES.includes(state as WorkState);
}

export function isTerminalState(state: TaskState): state is TerminalState {
  return TERMINAL_STATES.includes(state as TerminalState);
}

/**
 * Reasons a task may end up in `failed`. `cancelled` is a separate terminal
 * state — cancellation is not failure.
 */
export const FAILURE_REASONS = [
  'unrecoverable_error',
  'integration_outage',
  'max_retries',
  'timeout',
  'invalid_input',
] as const;

export type FailureReason = (typeof FAILURE_REASONS)[number];

/**
 * Actor enum — who is performing the work in a work state. The state machine
 * never branches on actor; only the handler does.
 */
export const ACTORS = ['ai', 'human', 'system'] as const;
export type Actor = (typeof ACTORS)[number];

/**
 * Allowed transitions between states. Used by the state machine validator and
 * the API to compute available actions for a task.
 *
 * `paused` is reachable from any work state and resumes to either the same
 * state (clarification, guardrail) or a different state (approval → execute,
 * review → succeeded). The destination depends on the pause reason and
 * resolution; see packages/shared/src/pauses.ts.
 */
export const ALLOWED_TRANSITIONS: Readonly<Record<TaskState, readonly TaskState[]>> = {
  triage: ['plan', 'paused', 'failed', 'cancelled'],
  plan: ['execute', 'paused', 'failed', 'cancelled'],
  execute: ['succeeded', 'paused', 'failed', 'cancelled'],
  paused: ['triage', 'plan', 'execute', 'succeeded', 'failed', 'cancelled'],
  // Terminal states are not directly transitioned out of; retry creates a
  // separate `actions` API path that re-enters at a chosen state.
  succeeded: [],
  failed: [],
  cancelled: [],
};

export function canTransition(from: TaskState, to: TaskState): boolean {
  return ALLOWED_TRANSITIONS[from].includes(to);
}
